<script setup lang="ts">

import { computed } from 'vue';
import OverflowTooltip from '@/components/OverflowTooltip.vue';
import RecordLink from '@/components/RecordLink.vue';
import { entityDisplayNameFor } from '@/composables/entityColumns';

const props = defineProps<{
  field: string;
  dataType: string;
  data: any;
}>();

// Get the base field name (e.g., 'location_zone' from 'location_zone.display_string')
const baseFieldName = computed(() => {
  return props.field.split('.')[0];
});

// Get the foreign model object from data
const foreignModelObject = computed(() => {
  const baseField = baseFieldName.value;
  return props.data[baseField];
});

// Check if foreign model has an id
const foreignModelId = computed(() => {
  const modelObj = foreignModelObject.value;
  if (modelObj && typeof modelObj === 'object' && !Array.isArray(modelObj) && 'id' in modelObj) {
    return modelObj.id;
  }
  return null;
});

// Extract model type from dataType (e.g., "LocationZone" from "LocationZone" or "LocationZone[]")
const foreignModelType = computed(() => {
  if (props.dataType && typeof props.dataType === 'string') {
    // Remove array suffix if present
    const modelType = props.dataType.replace('[]', '');
    // Check if it starts with uppercase (indicating a model type)
    if (modelType && modelType[0] === modelType[0].toUpperCase()) {
      return modelType;
    }
  }
  return null;
});

const displayValue = computed(() => {
    // props.field can be a dot notation string like 'user.display_string'
    const parts = props.field.split('.');
    const attributes = parts.reduce((acc, key) => {
      if (Array.isArray(acc)) {
        return acc.map(item => item?.[key]);
      }
      return acc?.[key];
    }, props.data);

    if (!attributes) {
        return '';
    }

    // Arrays
    if (Array.isArray(attributes)) {
        return attributes.join(', ');
    }

    // If it's an object, try some default properties
    if (typeof attributes === 'object' && !Array.isArray(attributes)) {
        if (attributes.display_string) {
            return attributes.display_string;
        } else if (attributes.name) {
            return attributes.name;
        }
    }

    return attributes;
});

</script>

<template>
  <!-- The hover area reaches 4px past the text on each side: padding in, margin
       out. The width has to grow by the same 8px, or the negative margin only
       shifts the box left and a right-aligned value ends 8px short of its edge. -->
  <OverflowTooltip
    :deps="displayValue"
    trigger-class="py-1 px-1 -mx-1 w-[calc(100%+0.5rem)]! max-w-[calc(100%+0.5rem)]!"
    side="top"
    align="start"
    :collision-padding="16"
  >
    <!-- The shared RecordLink decides how the name opens: a link to the record's
         page with the side-peek button beside it, the side peek alone for a type
         with no page the user may open, or plain text. -->
    <RecordLink
      v-if="foreignModelType && foreignModelId !== null"
      :entity-id="foreignModelType"
      :record-id="foreignModelId"
      :label="`${entityDisplayNameFor(foreignModelType)} ${displayValue}`"
      data-testid="foreign-model-link"
    >
      {{ displayValue }}
    </RecordLink>
    <template v-else>{{ displayValue }}</template>
    <template #tooltip>{{ displayValue }}</template>
  </OverflowTooltip>
</template>
